import { useEffect, useRef } from "react";
import "./Particles.css";

export default function Particles({
  particleColors = ["#ffffff"],
  particleCount = 200,
  particleSpread = 10,
  speed = 0.13,
  particleBaseSize = 100,
  moveParticlesOnHover = true,
  alphaParticles = false,
  disableRotation = false,
  pixelRatio = 1,
}) {
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({
    x: -9999,
    y: -9999,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    let animationFrame;
    let width = window.innerWidth;
    let height = window.innerHeight;

    function random(min, max) {
      return Math.random() * (max - min) + min;
    }

    function resizeCanvas() {
      width = window.innerWidth;
      height = window.innerHeight;

      canvas.width = width * pixelRatio;
      canvas.height = height * pixelRatio;

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);

      criarParticulas();
    }

    function criarParticulas() {
      const novasParticulas = [];

      for (let i = 0; i < particleCount; i += 1) {
        const grande = Math.random() > 0.9;

        novasParticulas.push({
          x: random(0, width),
          y: random(0, height),
          originalX: random(0, width),
          originalY: random(0, height),
          radius: grande
            ? random(2.4, 4.8) * (particleBaseSize / 100)
            : random(0.8, 1.8) * (particleBaseSize / 100),
          vx: random(-0.25, 0.25) * speed * 6,
          vy: random(-0.25, 0.25) * speed * 6,
          alpha: alphaParticles ? random(0.35, 0.95) : random(0.75, 1),
          color: particleColors[i % particleColors.length],
          angle: random(0, Math.PI * 2),
          orbit: random(0.2, particleSpread),
        });
      }

      particlesRef.current = novasParticulas;
    }

    function handleMouseMove(event) {
      mouseRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
    }

    function handleMouseLeave() {
      mouseRef.current = {
        x: -9999,
        y: -9999,
      };
    }

    function desenharParticula(particle) {
      context.beginPath();

      const gradient = context.createRadialGradient(
        particle.x,
        particle.y,
        0,
        particle.x,
        particle.y,
        particle.radius * 3,
      );

      gradient.addColorStop(0, particle.color);
      gradient.addColorStop(0.45, particle.color);
      gradient.addColorStop(1, "rgba(255,255,255,0)");

      context.fillStyle = gradient;
      context.globalAlpha = particle.alpha;

      context.arc(
        particle.x,
        particle.y,
        particle.radius * 2.2,
        0,
        Math.PI * 2,
      );

      context.fill();

      context.beginPath();
      context.fillStyle = particle.color;
      context.globalAlpha = Math.min(1, particle.alpha + 0.15);

      context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
      context.fill();

      context.globalAlpha = 1;
    }

    function atualizarParticula(particle) {
      if (!disableRotation) {
        particle.angle += 0.003 * speed;
        particle.x += Math.cos(particle.angle) * 0.08 * particle.orbit;
        particle.y += Math.sin(particle.angle) * 0.08 * particle.orbit;
      }

      particle.x += particle.vx;
      particle.y += particle.vy;

      if (particle.x < -20) particle.x = width + 20;
      if (particle.x > width + 20) particle.x = -20;
      if (particle.y < -20) particle.y = height + 20;
      if (particle.y > height + 20) particle.y = -20;

      if (moveParticlesOnHover) {
        const dx = particle.x - mouseRef.current.x;
        const dy = particle.y - mouseRef.current.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const forceRadius = 120;

        if (distance < forceRadius) {
          const force = (forceRadius - distance) / forceRadius;
          const angle = Math.atan2(dy, dx);

          particle.x += Math.cos(angle) * force * 2.4;
          particle.y += Math.sin(angle) * force * 2.4;
        }
      }
    }

    function animar() {
      context.clearRect(0, 0, width, height);

      particlesRef.current.forEach((particle) => {
        atualizarParticula(particle);
        desenharParticula(particle);
      });

      animationFrame = requestAnimationFrame(animar);
    }

    resizeCanvas();
    animar();

    window.addEventListener("resize", resizeCanvas);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", resizeCanvas);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [
    particleColors,
    particleCount,
    particleSpread,
    speed,
    particleBaseSize,
    moveParticlesOnHover,
    alphaParticles,
    disableRotation,
    pixelRatio,
  ]);

  return (
    <div className="particles-container">
      <canvas ref={canvasRef} />
    </div>
  );
}
